import { Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Animal } from '../../animal';
import { AnimalEvent } from '../animal-event';
import { Mating, MatingSummary } from './mating';
import {
    ApiService,
    Filter,
    FilterOp,
    NetworkStatusService,
    SortMode,
    SortOrder
} from '@homeleite/arch';
import {
    BaseIndexedDbRepository,
    SynchronizationService
} from 'libs/arch/src/lib/repositories/indexed-db';

@Injectable({ providedIn: 'root' })
export class MatingRepository extends BaseIndexedDbRepository<MatingSummary, Mating> {
    protected override readonly storeName: string = 'matings';

    constructor(
        dbService: NgxIndexedDBService,
        synchronizationService: SynchronizationService,
        private networkStatus: NetworkStatusService,
        private apiService: ApiService
    ) {
        super(dbService, synchronizationService);

        this.registerAnimalReferences('animalId', 'embryDonorId');
    }

    public override insert(mating: Mating): Observable<Mating> {
        return super.insert(mating).pipe(
            tap(savedMating => {
                const transformedEvent: AnimalEvent = {
                    id: savedMating.id,
                    date: savedMating.date,
                    animal: savedMating.animal,
                    type: "MATING",
                    created: savedMating.created
                };

                this.dbService.add('animalEvents', transformedEvent).subscribe({
                    error: (err) => {
                        console.error('Falha ao adicionar o evento de Cobertura em animalEvents:', err);
                    }
                });
            })
        );
    }

    protected override mapToEntity(dbItem: Mating): Mating {
        return Mating.fromJson(dbItem);
    }

    protected override mapToSummary(item: Mating): MatingSummary {
        return new MatingSummary(item.id, item.animal, item.father, item.date);
    }

    protected override getSearchableFields(): string[] {
        return ['animal.name', 'animal.register', 'father.name', 'father.register', 'date', 'type'];
    }

    protected override getResourcePath(): string {
        return 'matings';
    }

    public findByAnimal(animal: Animal): Observable<Mating[]> {
        const animalId: string | undefined = animal?.id;
        if (!animalId) {
            return of([]);
        }

        const getFromDb = () => {
            return this.executeIndexedDbQuery({
                filters: [new Filter('animal.id', FilterOp.EQ, animalId)],
                sortOrders: [new SortOrder('date', SortMode.DSC)]
            });
        };

        if (!this.networkStatus.isOnline()) {
            return getFromDb();
        }

        return this.fetchMatingsBy(animalId)
            .pipe(catchError(() => getFromDb()));
    }

    private fetchMatingsBy(animalId: string): Observable<Mating[]> {
        return this.apiService.root
            .path(this.getResourcePath())
            .path('animal')
            .path(animalId)
            .getJson<any[]>()
            .pipe(
                map((response) =>
                    Array.isArray(response) ? response.map((item) => Mating.fromJson(item)) : []
                ),
                tap((matings) => {
                    if (!matings?.length) return;

                    const integrationMatings = matings.filter((mating) => {
                        const { auditData } = mating;
                        return (
                            auditData?.createdByIntegration || (auditData?.createdByApp && auditData?.modifiedByIntegration)
                        );
                    });

                    if (integrationMatings.length > 0) {
                        this.saveLocally(integrationMatings).subscribe({
                            error: (err) =>
                                console.error('Falha ao salvar coberturas em lote:', err)
                        });
                    }
                })
            );
    }
}
